<template>
  <div class="ou-columns-form">
    <PaymentForm>
      <div class="left-panel" v-if="pledge">
        <div class="inner-panel innerpanelsa">
          <div class="campaign-info campaign-info__sub">
            {{ startCase(pledge.department) }} - {{ pledge.campaignName }}
          </div>
          <div class="pledge-info">
            <div class="name">{{ pledge.name }} {{ pledge.lastName }}</div>
            <div class="balance-info">
              Your account balance:
              <MoneyFormat :value="pledge.amount - pledge.totalPaid" /> outstanding of
              your <MoneyFormat :value="pledge.amount" /> pledge
            </div>
          </div>
          <div class="amount-info">
            <div class="amount">
              Thanks you so much for your generous pledge! Your support makes the
              difference.
            </div>
            <PaymentOptions :addSplit="!hasActiveBillingPlan(pledge.billingPlan)" splitText="Monthly Split Payment" />
            <div class="amount__simple">
              <AmountSelectorSimple />
              <label class="control-label">Total Amount Due</label>
            </div>
            <ErrorLabel field="amount" />
          </div>
        </div>
      </div>
      <div class="right-panel">
        <div class="inner-panel">
          <h2>Billing Information</h2>
										<FormRow>
												<FormLabel class="control-label">
													School Name
											</FormLabel>
										</FormRow>
										<FormRow>
												<!-- Remove event listeners -->
												<SchoolAutocomplete />
										</FormRow>
                    <ErrorLabel field="custom.schoolName" />   
                    <br>       
          <FormRow inline>
            <FormCol field="firstName" />
            <FormCol field="lastName" />
          </FormRow>
          <FormRow inline>
            <FormCol field="email" />
            <FormCol field="phone" />
          </FormRow>
          <FormRow inline>
                      <Consent privacy="https://www.ou.org/privacy-policy/"/>
                  </FormRow>            
          <DonorAddress />
          <br />
          <FormRow inline>
            <FormCol field="card" :showLabel="false" />
          </FormRow>
          <br /><br />
          <PaymentButton showAmount btnText="Pay" />
        </div>
      </div>
    </PaymentForm>
  </div>
</template>
<script lang="ts">
import { defineComponent, inject, computed, type Ref } from "vue";
import { usePaymentForm } from "~/shared/use-payment-form";
import { hasActiveBillingPlan } from "~/shared/pledge-form";
import startCase from "lodash/startCase";
import SchoolAutocomplete from '~/components/SchoolAutocomplete.vue'

export default defineComponent({
  components: {
    SchoolAutocomplete
  },
  setup(props) {
    const { form, setValue, getValue } = usePaymentForm();
    const pledge = inject("pledge") as Ref<any>;

    /**
     * Calculate allowed split months based on the school year, which always starts in September.
     * School year end months:
     * - TeachCoalition_NYS, TeachCoalition_Gov_Max, TeachCoalition_NJ, TeachCoalition_PA: September – June (ends June 30)
     * - TeachCoalition_FL: September – May (ends May 31)
     *
     * Maximum split is 9 months, but limited to remaining months in school year.
     * Off-season (July/August) is billed as if the upcoming September school year had started.
     * If only the final month of the school year is left, the split option is hidden.
     */
    const department = computed(() => (getValue as any)('applications.0.department') || pledge.value?.department);

    // 1-indexed months (January = 1, ..., December = 12)
    const SCHOOL_YEAR_START_MONTH = 9;
    const MAX_SPLIT_MONTHS = 9;

    // FL ends in May (5), all others end in June (6)
    const schoolYearEndMonth = computed(() => department.value === 'TeachCoalition_FL' ? 5 : 6);

    // Months left in the school year, including the current month
    const monthsRemaining = computed(() => {
      const endMonth = schoolYearEndMonth.value;
      const currentMonth = new Date().getMonth() + 1;

      // Between the end month and September the year hasn't started yet, so bill from September
      const billingMonth =
        currentMonth >= SCHOOL_YEAR_START_MONTH || currentMonth <= endMonth
          ? currentMonth
          : SCHOOL_YEAR_START_MONTH;

      // Sept–Dec wraps into the next calendar year, e.g. September (9) to June (6) = 10 months
      return billingMonth > endMonth
        ? endMonth + 12 - billingMonth + 1
        : endMonth - billingMonth + 1;
    });

    const splitMonths = computed(() => Math.min(MAX_SPLIT_MONTHS, monthsRemaining.value));

    // Nothing to split when the last month of the school year is the only one left
    const canShowSplit = computed(() => splitMonths.value > 1);

    return {
      form,
      setValue,
      getValue,
      pledge,
      startCase,
      hasActiveBillingPlan,
      splitMonths,
      canShowSplit,
    };
  },
});
</script>

<style scoped lang="scss">
.ou-columns-form {
  background: white;
  position: relative;
  .logo {
    width: 100px;
    opacity: 0.5;
    position: relative;
    margin-bottom: 2rem;
    left: 30px;
    top: 20px;
  }
  h2 {
    font-weight: 600;
    margin-bottom: 1rem;
    font-size: 16px;
    color: #0f346c;
    margin-top: 0;
    border-bottom: 1px solid #efefef;
    padding-bottom: 0.5rem;
  }
  > form {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }
  > form > div {
    flex: 1;
    padding: 2rem 0px;
    display: flex;
    justify-items: center;
    flex-direction: column;
      @media (min-width: 720px) {
        padding: 2rem;
      }
  }
  .inner-panel {
    max-width: 550px;
    margin: auto;
    width: 100%;
  }
  @media (min-width: 720px) {
    .logo {
      position: absolute;
      width: 100px;
      opacity: 0.5;
    }
    > form {
      flex-direction: row;
    }
    .right-panel {
      box-shadow: -1px 0 50px rgba(0, 0, 0, 0.1);
    }
  }
}
::v-deep .ou-donation-btn {
  width: 100%;
  justify-content: center;
  background: #0d346c;
  padding: 0.8rem;

  &:hover {
    background: black;
  }
}
.name {
  font-weight: bold;
  font-size: 1.3rem;
}
.address {
  margin-top: 0.3rem;
  color: #999;
  font-size: 0.8rem;
}
.amount {
  font-size: 1.6rem;
  font-weight: 900;
  line-height: normal;
  margin-bottom: 2rem;
}
.pledge-info {
  margin-bottom: 2rem;
}
.balance-info {
  margin-left: 0px;
  font-size: 0.8rem;
  font-weight: normal;
  margin-top: 0.5rem;
  color: #999;
  ::v-deep .money_format {
    display: inline-block;
    font-weight: bold;
  }
}
.ou-columns-form ::v-deep .styled-radio-container .radio-part {
  max-width: 50%;
}
.ou-columns-form
  ::v-deep
  .styled-radio-container--ou-donation.styled-radio-container
  input[type="radio"]:checked
  + label {
  background: #0d346c;
}
.campaign-info {
  font-size: 0.8rem;
  display: inline-block;
  padding: 0.4rem;
  border-radius: 3px;
  color: #f7f7f7;
  margin-bottom: 1rem;
  background: #222;
  font-weight: 400;
}

.amount__simple ::v-deep .oudonate-other-amount {
  input[type="number"] {
    box-sizing: border-box;
    height: 44px;
    width: 100%;
    display: block;
    border: 1px solid #ddd;
    padding: 0 10px;
    border-radius: 4px;
    background: #f7f9fb;
    transition: all 0.3s ease-in-out;
    font-size: 16px;
    color: #222;

    &:hover {
      box-shadow: none;
      border-color: $color-blue;
    }

    &:focus {
      border-color: $color-blue;
      box-shadow: none;
      color: #222;
      background: white;
      box-shadow: 0 0 3px rgba(56, 93, 163, 0.5);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      border: 1px solid #e4e4e4;
      background: #f3f2f2;
    }

    @media only screen and (min-width: 720px) {
      height: 42px;
    }
  }

  input::-webkit-input-placeholder {
    color: #ccc;
  }
  input::-moz-placeholder {
    color: #ccc;
  }
  input:-ms-input-placeholder {
    color: #ccc;
  }
}

.amount-info ::v-deep select {
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: #f7f9fb;
  color: #222;
  cursor: pointer;
  width: 100%;
  outline: 0;
  box-sizing: border-box;
  min-width: 120px;
  transition: border-color 0.3s ease-in-out;
  font-size: 16px;
  align-items: center;
  box-shadow: none;
  display: inline-flex;
  height: 44px;
  justify-content: flex-start;
  padding: 0 10px;
  position: relative;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml;base64,PHN2ZyBpZD0iTGF5ZXJfMSIgZGF0YS1uYW1lPSJMYXllciAxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0Ljk1IDEwIj48ZGVmcz48c3R5bGU+LmNscy0ye2ZpbGw6IzQ0NDt9PC9zdHlsZT48L2RlZnM+PHRpdGxlPmFycm93czwvdGl0bGU+PHBvbHlnb24gY2xhc3M9ImNscy0yIiBwb2ludHM9IjEuNDEgNC42NyAyLjQ4IDMuMTggMy41NCA0LjY3IDEuNDEgNC42NyIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMiIgcG9pbnRzPSIzLjU0IDUuMzMgMi40OCA2LjgyIDEuNDEgNS4zMyAzLjU0IDUuMzMiLz48L3N2Zz4=");
  background-repeat: no-repeat;
  background-position: 95% 50%;
  @media only screen and (min-width: 720px) {
    height: 42px;
  }

  &:hover {
    border-color: $color-blue;
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    border: 1px solid #e4e4e4;
    background: #f3f2f2;
  }
  &:focus {
    border-color: $color-blue;
    box-shadow: none;
    color: #222;
    background: white;
    box-shadow: 0 0 3px rgba(56, 93, 163, 0.5);
  }
}
.control-label {
  font-size: 14px;
    color: #222;
    padding-top: 0px;
    padding-bottom: 0px;
    margin-bottom: 0px;
    line-height: normal;
    display: block;
}

.left-panel{
  @media only screen and (max-width: 720px) {
    padding: 0px !important;
  }
}


.amount__simple ::v-deep .oudonate-other-amount .amount_other__wrapper:before {
    position: absolute;
    padding: 11px 8px;
    border-right: 1px solid #ddd;
    height: 19px;
    background: #f7f9fb;
    top: 0;
    font-size: 16px;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.amount__simple ::v-deep .oudonate-other-amount:before {
    margin-bottom: 2px;
}
</style>
